import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const BlogPage = () => {
  const { getToken } = useAuth();
  const [posts, setPosts] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    status: 'draft',
    category: 'Product Engineering'
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/blog/posts`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setPosts(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const url = editingPost
        ? `${apiUrl}/api/admin/blog/posts/${editingPost.id}`
        : `${apiUrl}/api/admin/blog/posts`;

      const response = await fetch(url, {
        method: editingPost ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchPosts();
        setShowEditor(false);
        setEditingPost(null);
        setFormData({ title: '', content: '', excerpt: '', status: 'draft', category: 'Product Engineering' });
      }
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      status: post.status,
      category: post.category
    });
    setShowEditor(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-nunito text-white">Blog Posts</h1>
          <p className="mt-1 text-orange-300 font-nunito">{posts.length} total posts</p>
        </div>
        <button
          onClick={() => {
            setShowEditor(true);
            setEditingPost(null);
            setFormData({ title: '', content: '', excerpt: '', status: 'draft', category: 'Product Engineering' });
          }}
          className="px-4 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-600 transition font-nunito flex items-center gap-2"
        >
          <span>➕</span>
          New Post
        </button>
      </div>

      {/* Posts List */}
      <div className="grid grid-cols-1 gap-6">
        {posts.map((post) => (
          <div key={post.id} className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-bold text-white font-nunito">{post.title}</h3>
                  <span className={`px-3 py-1 text-xs rounded-full font-nunito ${
                    post.status === 'published'
                      ? 'bg-green-500/20 text-green-300'
                      : 'bg-orange-500/20 text-orange-300'
                  }`}>
                    {post.status}
                  </span>
                </div>
                <p className="text-orange-300 font-nunito mb-3">{post.excerpt}</p>
                <div className="flex items-center gap-4 text-sm text-orange-400 font-nunito">
                  <span>📂 {post.category}</span>
                  <span>📅 {new Date(post.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(post)}
                  className="px-3 py-1 bg-white/10 border border-white/10 text-orange-100 rounded-lg hover:bg-white/15 transition font-nunito text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="px-3 py-1 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg hover:bg-red-500/30 transition font-nunito text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}

        {posts.length === 0 && (
          <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-12 text-center">
            <p className="text-orange-400 font-nunito">No blog posts yet. Create your first post!</p>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-orange-900/90 border border-white/10 rounded-2xl max-w-4xl w-full my-8">
            <div className="p-6">
              <h2 className="text-2xl font-bold font-nunito text-white mb-6">
                {editingPost ? 'Edit Post' : 'Create New Post'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white placeholder-orange-400/50 focus:outline-none focus:border-amber-500/50 font-nunito"
                    placeholder="Post title..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                    Excerpt (Short summary)
                  </label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    required
                    rows="2"
                    className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white placeholder-orange-400/50 focus:outline-none focus:border-amber-500/50 font-nunito"
                    placeholder="Brief description for newsletter and previews..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                    Content
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows="12"
                    className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white placeholder-orange-400/50 focus:outline-none focus:border-amber-500/50 font-nunito"
                    placeholder="Write your post content here..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-amber-500/50 font-nunito"
                    >
                      <option>Product Engineering</option>
                      <option>Cloud Solutions</option>
                      <option>Software Development</option>
                      <option>Case Studies</option>
                      <option>Industry Insights</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-amber-500/50 font-nunito"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-600 transition font-nunito"
                  >
                    {editingPost ? 'Update Post' : 'Create Post'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditor(false);
                      setEditingPost(null);
                    }}
                    className="px-4 py-2 bg-white/10 border border-white/10 text-orange-100 rounded-lg hover:bg-white/15 transition font-nunito"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPage;
